import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { GooeyText } from "@/components/ui/gooey-text-morphing";

const textInitial = { opacity: 0, y: 20 };
const textAnimate = { opacity: 1, y: 0 };

const morphTexts = ["Optimized Ads", "Lead Intake", "Qualification", "Speed-to-Lead", "Nurture Sequences", "Follow-up", "Appointment Prep", "Quality Feedback"];

export function CastleOnSand() {
  const reduced = useReducedMotion();
  const textTransition = useMemo(() => (reduced ? {} : { duration: 0.5, delay: 0.2 }), [reduced]);
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="foundation" className="relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid md:grid-cols-2 gap-4 md:gap-12 items-center">
          {/* Gooey morphing visual */}
          <div className="flex flex-col items-center text-center">
            <div className="h-[64px] md:h-[120px] flex items-center justify-center">
              {reduced || !inView ? (
                <span className="text-4xl md:text-6xl font-bold text-primary">
                  Optimized Ads
                </span>
              ) : (
                <GooeyText
                  texts={morphTexts}
                  morphTime={1.2}
                  cooldownTime={0.8}
                  textClassName="text-4xl md:text-6xl font-bold text-primary"
                />
              )}
            </div>
          </div>

          {/* Text */}
          <motion.div
            initial={reduced ? {} : textInitial}
            whileInView={reduced ? {} : textAnimate}
            viewport={{ once: true }}
            transition={textTransition}
            className="will-change-transform"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-tight">
              A great firm deserves a solid{" "}
              <span className="relative inline-block">
                foundation
                <span className="absolute left-0 bottom-0 w-full h-1 bg-primary rounded-full" />
              </span>
              .
            </h2>
            <div className="mt-4 space-y-3 text-sm md:text-lg text-muted-foreground leading-relaxed">
              <p>Immigration firms are built to win cases. Winning clients takes a different system — one built specifically for that purpose.</p>
              <p>Referrals are great when they come. A pipeline gives you consistent flow regardless of the season.</p>
              <p>The firm is solid. The work is excellent. Now the foundation — built on a real system — can match that quality.</p>
              <p>FuzionLabs builds the concrete underneath. A system that brings clients in consistently, so the firm can focus on the cases.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
